import React, { useCallback, useState, type ReactNode } from 'react';
import { cssMap } from '@compiled/react';
import { Box } from '@atlaskit/primitives/compiled';
import { useIntl } from '@atlassian/jira-intl';
import { ProjectsAccess } from '@atlassian/project-picker/ProjectsAccess';
import { useOrgId } from '@atlassian/jira-router-resources-navigation-org-id/src/index.tsx';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { TownsquareProjectAri } from '@atlassian/ari/townsquare/project';
import {
	useAnalyticsEvents,
	fireUIAnalytics,
	fireTrackAnalytics,
	ContextualAnalyticsData,
} from '@atlassian/jira-product-analytics-bridge';
import { fg } from '@atlassian/jira-feature-gating';

import { RestrictedIcon } from '../../restricted/index.tsx';
import restrictedMessages from '../../restricted/messages.tsx';
import { HoverPopup } from '../../hover-popup/index.tsx';
import { PrivateProjectPopup } from '../../private-project-popup/index.tsx';
import { InlineSmartLink, type InlineSmartLinkProps } from './InlineSmartLink.tsx';

const styles = cssMap({
	container: {
		display: 'flex',
		justifyContent: 'center',
	},
});

type ErrorArg = { status?: string };

type Props = InlineSmartLinkProps & {
	initialHasNoAccess?: boolean;
	projectAri?: string;
	showNoAccessPopup?: boolean;
	isPrivate?: boolean;
	onHandleNoAccess?: () => void;
};

export const SimpleInlineSmartLink = ({
	initialHasNoAccess = false,
	showNoAccessPopup = true,
	isPrivate = false,
	onHandleNoAccess,
	...props
}: Props): React.JSX.Element => {
	const [hasNoAccess, setHasNoAccess] = useState<boolean>(initialHasNoAccess || isPrivate);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const handleError = useCallback(
		(arg: ErrorArg) => {
			fireTrackAnalytics(createAnalyticsEvent({}), 'atlasProjectSmartLink failed', {
				errorStatus: arg.status,
				isPrivate,
				initialHasNoAccess,
				hasNoAccess,
			});
			if (arg?.status === 'forbidden' || arg?.status === 'errored') {
				setHasNoAccess(true);
				onHandleNoAccess?.();
			}
		},
		[createAnalyticsEvent, isPrivate, initialHasNoAccess, onHandleNoAccess, hasNoAccess],
	);

	const handleResolve = useCallback(() => {
		setHasNoAccess(false);
		fireTrackAnalytics(createAnalyticsEvent({}), 'atlasProjectRestrictedSmartLink resolved', {
			isPrivate,
			initialHasNoAccess,
		});
	}, [createAnalyticsEvent, isPrivate, initialHasNoAccess]);

	if (hasNoAccess) {
		if (fg('smart-link-custom-hover-card-content')) {
			return (
				<RestrictedSmartLink
					{...props}
					initialHasNoAccess={initialHasNoAccess}
					showNoAccessPopup={showNoAccessPopup}
					isPrivate={isPrivate}
					onResolve={handleResolve}
					onError={handleError}
				/>
			);
		}

		return (
			<OldRestrictedSmartLink
				{...props}
				initialHasNoAccess={initialHasNoAccess}
				showNoAccessPopup={showNoAccessPopup}
				isPrivate={isPrivate}
				onResolve={handleResolve}
				onError={handleError}
			/>
		);
	}

	return <InlineSmartLink {...props} onError={handleError} />;
};

const RestrictedSmartLink = ({
	initialHasNoAccess,
	projectAri,
	showNoAccessPopup,
	isPrivate,
	...props
}: Props) => {
	const { formatMessage } = useIntl();
	const { data: orgId } = useOrgId();
	const accountId = useAccountId();
	const cloudId = projectAri ? TownsquareProjectAri.parse(projectAri).siteId : undefined;
	// only show private project popup if the project is private and initially the user has site access
	const showPrivateProject = isPrivate && !initialHasNoAccess;

	return (
		<ContextualAnalyticsData
			attributes={{
				hoverCardName: showPrivateProject
					? 'atlasProjectPrivateProjectPopup'
					: 'atlasProjectProjectsAccessPopup',
			}}
		>
			<InlineSmartLink
				{...props}
				text={formatMessage(restrictedMessages.restrictedProject)}
				icon={<RestrictedIcon />}
				hideRetry
				showHoverPreview={showNoAccessPopup}
				hoverPreviewOptions={{
					render: () =>
						showPrivateProject ? (
							<PrivateProjectPopup />
						) : (
							<Box xcss={styles.container}>
								<ProjectsAccess
									orgId={orgId}
									accountId={accountId ?? undefined}
									requestAccessSimplifiedUI={false}
									cloudId={cloudId}
								/>
							</Box>
						),
				}}
			/>
		</ContextualAnalyticsData>
	);
};

const OldRestrictedSmartLink = ({
	initialHasNoAccess,
	projectAri,
	showNoAccessPopup,
	isPrivate,
	...props
}: Props) => {
	const { formatMessage } = useIntl();

	const renderNoAccessSmartLink = (
		<InlineSmartLink
			{...props}
			text={formatMessage(restrictedMessages.restrictedProject)}
			icon={<RestrictedIcon />}
			hideRetry
			showHoverPreview={false}
		/>
	);

	if (!showNoAccessPopup) return renderNoAccessSmartLink;

	return (
		<HoverPopupIfRestricted
			projectAri={projectAri}
			// only show private project popup if the project is private and initially the user has site access
			showPrivateProject={isPrivate && !initialHasNoAccess}
		>
			{renderNoAccessSmartLink}
		</HoverPopupIfRestricted>
	);
};

type HoverPopupIfRestrictedProps = {
	projectAri?: string;
	showPrivateProject?: boolean;
	children: ReactNode;
};

const HoverPopupIfRestricted = ({
	projectAri,
	showPrivateProject,
	children,
}: HoverPopupIfRestrictedProps) => {
	const { data: orgId } = useOrgId();
	const accountId = useAccountId();
	const cloudId = projectAri ? TownsquareProjectAri.parse(projectAri).siteId : undefined;
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const handleVisibilityChange = useCallback(
		(isVisible: boolean) => {
			if (isVisible) {
				fireUIAnalytics(createAnalyticsEvent({}), 'atlasProjectAccessPopup viewed', {
					showPrivateProject,
				});
			}
		},
		[createAnalyticsEvent, showPrivateProject],
	);

	return (
		<HoverPopup
			renderContent={
				showPrivateProject ? (
					<PrivateProjectPopup />
				) : (
					<ProjectsAccess
						orgId={orgId}
						accountId={accountId ?? undefined}
						requestAccessSimplifiedUI={false}
						cloudId={cloudId}
					/>
				)
			}
			onVisibilityChange={handleVisibilityChange}
		>
			{children}
		</HoverPopup>
	);
};
